/**
 * Pitch-shaped lineups visualization, SofaScore/FotMob inspired.
 *
 *  - Vertical (portrait) pitch with a proper 2:3 aspect ratio.
 *  - Home team on the TOP half, own goal at the TOP edge.
 *  - Away team on the BOTTOM half, own goal at the BOTTOM edge.
 *  - Players positioned with % coordinates so goalkeepers sit right on top of
 *    their goal lines and forwards hug the halfway line.
 *  - Field details: alternating grass stripes, penalty box + goal box,
 *    penalty spot & D-arc, corner arcs, center circle, ambient rim light.
 *  - Player badge: shirt-shaped disc with big jersey number, cognome in a
 *    dark pill under. Yellow-card and scorer picks get overlay icons.
 */
import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { InviteFriendsModal } from './ReferralModals';

// iter182 — Cross-check frontend: se un player del card-prediction non è
// nella XI mostrata sul pitch, azzeriamo il pick. Doppia sicurezza sopra
// il filtro backend (`_filter_prediction_against_xi`), che può fallire
// se `_current_displayed_lineups` restituisce una XI diversa da quella
// che l'utente vede (race su fetch API-Football / DIY / web-probable).
const _normalize = (s) => (s || '')
  .normalize('NFKD')
  .replace(/[\u0300-\u036f]/g, '')
  .replace(/[.']/g, '')
  .trim()
  .toLowerCase();

const _tokens = (s) => {
  const set = new Set();
  for (const t of _normalize(s).split(/\s+/)) if (t.length >= 3) set.add(t);
  return set;
};

const fuzzyInXi = (name, xiNames) => {
  const targetFull = _normalize(name);
  const targetTokens = _tokens(name);
  if (!targetFull || targetTokens.size === 0) return false;

  // iter210 — Se il target ha un'iniziale (es. "S. Charles" → "s") e la
  // XI contiene più giocatori con lo stesso cognome, il match deve
  // rispettare l'iniziale. Se il target NON ha iniziale (solo cognome),
  // teniamo la logica fuzzy legacy.
  const targetInit = firstInitial(name);
  const targetLast = lastName(name).toLowerCase();
  if (targetInit && targetLast) {
    const sameLastCands = xiNames.filter(
      (c) => lastName(c).toLowerCase() === targetLast,
    );
    if (sameLastCands.length >= 1) {
      // Preferisci il match con iniziale coerente.
      for (const c of sameLastCands) {
        const ci = firstInitial(c);
        if (!ci || ci === targetInit) return true;
      }
      // Nessun candidato con cognome uguale ha l'iniziale giusta →
      // NON matchare (evita falsi positivi con omonimi).
      return false;
    }
  }

  for (const cand of xiNames) {
    const candFull = _normalize(cand);
    if (!candFull) continue;
    if (candFull === targetFull) return true;
    const candTokens = _tokens(cand);
    for (const t of targetTokens) if (candTokens.has(t)) return true;
    for (const t of targetTokens) if (candFull.includes(t)) return true;
    for (const t of candTokens) if (targetFull.includes(t)) return true;
  }
  return false;
};

const parseFormation = (formation) => {
  if (!formation || typeof formation !== 'string') return null;
  const parts = formation.trim().split('-').map((n) => parseInt(n, 10));
  if (parts.some((n) => Number.isNaN(n) || n < 1 || n > 6)) return null;
  if (parts.reduce((a, b) => a + b, 0) > 10) return null;
  return parts;
};

const lastName = (name) => {
  if (!name) return '';
  const cleaned = String(name).replace('.', '').trim();
  const parts = cleaned.split(/\s+/);
  return parts.length ? parts[parts.length - 1] : cleaned;
};

// iter210 (2026-02) — Prima iniziale del nome per disambiguare omonimi
// nella XI (es. "Pierce Charles" #1 GK vs "Shea Charles" #19 MID).
// Ritorna la prima lettera del PRIMO token (in lowercase, senza accenti/punti).
// "S. Charles"     → "s"
// "Shea Charles"   → "s"
// "Charles"        → ""  (unico token → nessun'iniziale disambiguante)
const firstInitial = (name) => {
  if (!name) return '';
  const norm = String(name)
    .normalize('NFKD').replace(/[\u0300-\u036f]/g, '')
    .replace(/[.']/g, '').trim().toLowerCase();
  const parts = norm.split(/\s+/);
  if (parts.length < 2) return '';
  return (parts[0] || '')[0] || '';
};

// iter210 — Match "giusto" che tollera omonimi: same last name + same
// first initial (se disponibile su entrambi). Esempio Irlanda del Nord:
//   AI dice "S. Charles" → matcha SOLO "Shea Charles" (#19), NON il GK
//   "Pierce Charles" (#1). Se una delle due parti ha solo il cognome
//   (nessun primo token) accettiamo il match come fallback.
const samePlayer = (a, b) => {
  if (!a || !b) return false;
  if (lastName(a).toLowerCase() !== lastName(b).toLowerCase()) return false;
  const aInit = firstInitial(a);
  const bInit = firstInitial(b);
  if (aInit && bInit && aInit !== bInit) return false;
  return true;
};

const sameLastName = samePlayer;

/**
 * Role-aware bucketing so the pitch UI is robust against Gemini's
 * inconsistent output (missing `position` labels, players in the wrong
 * order, occasional duplicates).
 *
 * Contract:
 *   1. GK is ALWAYS anchored at index 0. Detection order:
 *        a) explicit `position` = P / GK
 *        b) shirt number = 1
 *        c) fallback: first player in the array
 *   2. Duplicates (same shirt number, or same last-name when number is
 *      missing) are removed keeping the first occurrence.
 *   3. Formation buckets are filled by role:
 *        - first bucket (defensive) → players with position D
 *        - last bucket (attackers)  → players with position A
 *        - middle buckets           → players with position C (in order)
 *      Unlabeled players spill over to fill whichever bucket is still
 *      short, then labelled players from over-full pools are used as
 *      last-resort filler.
 */
const _classifyRole = (p) => {
  const raw = String(p?.position || '').trim().toUpperCase();
  // API-Football uses G/D/M/F; Gemini uses P/D/C/A. Accept both plus
  // common English variants so a mislabelled GK never leaks into the
  // defensive row (this happened with Greif for Lyon and Samba for
  // Rennes when the DIY fallback returned `position: "G"`).
  if (raw === 'P' || raw === 'G' || raw === 'GK' || raw === 'PORTIERE') return 'GK';
  if (raw === 'D' || raw === 'DEF' || raw === 'DIFENSORE') return 'D';
  if (raw === 'C' || raw === 'M' || raw === 'MID' || raw === 'CENTROCAMPISTA') return 'C';
  if (raw === 'A' || raw === 'F' || raw === 'FW' || raw === 'ATT' || raw === 'ATTACCANTE') return 'A';
  return '?';
};

const _dedup = (xi) => {
  // Two entries count as the SAME player only when both the shirt
  // number and the last-name match. DIY sometimes assigns the same
  // `most_common` shirt number to two different players (e.g. squad
  // rotation reusing #4 across seasons) — treating them as the same
  // would drop one from the XI and leave an empty slot on the pitch.
  const seen = new Set();
  const out = [];
  for (const p of xi) {
    const num = p.number != null ? String(p.number) : '';
    const nm = String(p.name || '').toLowerCase().trim();
    const nmLast = nm.split(/\s+/).pop() || nm;
    // Compound key: number+last-name. Both empty → keep (unique by index).
    const key = num || nmLast ? `${num}|${nmLast}` : `_${out.length}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(p);
  }
  return out;
};

const splitByFormation = (xi, formation) => {
  const rows = parseFormation(formation);
  if (!rows) return null;

  // Only dedup when it doesn't leave holes: if the raw XI has 11 and
  // dedup removes even one, we'd end up with a missing slot on the
  // pitch (bug: Sparta Rotterdam 4-2-3-1 rendering with an empty spot
  // in the attacking midfield row). Keep the full XI in that case —
  // a duplicate rendered twice is FAR better UX than a missing player.
  const raw = xi || [];
  const deduped = _dedup(raw);
  const clean = deduped.length === raw.length ? deduped : raw;
  if (clean.length < 10) return null;

  // 1. Anchor the goalkeeper.
  let gkIdx = clean.findIndex((p) => _classifyRole(p) === 'GK');
  if (gkIdx === -1) {
    gkIdx = clean.findIndex((p) => Number(p.number) === 1);
  }
  if (gkIdx === -1) gkIdx = 0;
  const gk = clean[gkIdx];
  const outfield = clean.filter((_, i) => i !== gkIdx);

  // 2. Split outfield by role.
  const defs = outfield.filter((p) => _classifyRole(p) === 'D');
  const mids = outfield.filter((p) => _classifyRole(p) === 'C');
  const atts = outfield.filter((p) => _classifyRole(p) === 'A');
  const unks = outfield.filter((p) => _classifyRole(p) === '?');

  // 3. Bucket sizes per formation.
  //    rows = e.g. [3, 4, 2, 1] → defBucket=rows[0], attBucket=rows[N-1],
  //    everything in between is a mid/treq bucket.
  const buckets = rows.map(() => []);
  const lastIdx = rows.length - 1;

  const take = (queues, n) => {
    // Try each queue in order until n slots are filled.
    const out = [];
    for (const q of queues) {
      while (out.length < n && q.length) out.push(q.shift());
    }
    return out;
  };

  // Defenders first (only bucket 0). Priority: D → unknown → excess mids.
  buckets[0] = take([defs, unks, mids, atts], rows[0]);

  // Attackers (only last bucket). Priority: A → unknown → excess mids.
  if (lastIdx > 0) {
    buckets[lastIdx] = take([atts, unks, mids, defs], rows[lastIdx]);
  }

  // Middle buckets — fill from mids first, then unknown, then any leftover.
  for (let bi = 1; bi < lastIdx; bi++) {
    buckets[bi] = take([mids, unks, atts, defs], rows[bi]);
  }

  return { gk, rows: buckets };
};

// Official API-Football lineups: rows from grid "row:col" (row 1 = GK), each row ordered by
// column ascending = from the team's own left to its right.
const splitByGrid = (xi) => {
  const cells = (xi || []).map((p) => String(p?.grid || '').match(/^(\d+):(\d+)$/));
  if (xi.length !== 11 || cells.some((c) => !c)) return null;
  const byRow = new Map();
  xi.forEach((p, i) => {
    const r = Number(cells[i][1]);
    if (!byRow.has(r)) byRow.set(r, []);
    byRow.get(r).push({ p, c: Number(cells[i][2]) });
  });
  const keys = [...byRow.keys()].sort((a, b) => a - b);
  if (keys[0] !== 1 || byRow.get(1).length !== 1) return null;
  const rows = keys.slice(1).map((k) => byRow.get(k).sort((a, b) => a.c - b.c).map((e) => e.p));
  return { gk: byRow.get(1)[0].p, rows };
};

/**
 * Compute Y coordinates (in % of the full pitch height) for a team's rows.
 *
 * The band is ADAPTIVE: the more outfield rows, the wider the band, so
 * badge+name pills of one row never overlap the badges of the next.
 * The badge is ~30 px + name pill ~15 px ≈ 45 px, so on the smallest
 * expected pitch height (~495 px on mobile) we need at least ~10% per
 * row to keep them visually separated.
 *
 *  - 2 rows: home 16 → 38  (span 22, gap 22%)
 *  - 3 rows: home 14 → 40  (span 26, gap 13%)
 *  - 4 rows: home 11 → 44  (span 33, gap 11%)
 *  - 5 rows: home  8 → 46  (span 38, gap 9.5%)
 *
 * Away bands are mirrored around 50% (midfield line), so both halves
 * look symmetrical and the center circle stays visible in the middle.
 */
const computeRowYs = (side, rowsCount) => {
  if (rowsCount <= 0) return [];
  // iter175 — Bande outfield rialzate: il portiere ora è a y=7% (era 4)
  // quindi il difensore più alto deve partire da y≥14% per non
  // sovrapporsi al nome-pill del portiere. Con il pitch più alto
  // (60:105) ho comunque spazio abbondante fino a y=46% (attaccanti
  // a 4% dalla metà campo).
  const BANDS_HOME = {
    1: { start: 30, end: 30 },
    2: { start: 18, end: 40 },
    3: { start: 17, end: 42 },
    4: { start: 17, end: 44 },
    5: { start: 17, end: 46 },
  };
  const homeBand = BANDS_HOME[rowsCount] || BANDS_HOME[5];
  const bounds = side === 'home'
    ? homeBand
    : { start: 100 - homeBand.end, end: 100 - homeBand.start };
  if (rowsCount === 1) return [(bounds.start + bounds.end) / 2];
  const step = (bounds.end - bounds.start) / (rowsCount - 1);
  return Array.from({ length: rowsCount }, (_, i) => bounds.start + step * i);
};

const HALF_STYLE = {
  home: {
    shirt: 'from-cyan-400 via-cyan-500 to-cyan-700',
    ring: 'ring-cyan-200/40',
    stroke: 'border-cyan-200/70',
    accent: 'text-cyan-300',
    pillBg: 'bg-slate-900/85',
    pillText: 'text-cyan-100',
    numberText: 'text-white',
  },
  away: {
    shirt: 'from-orange-400 via-red-500 to-red-700',
    ring: 'ring-orange-200/40',
    stroke: 'border-orange-200/70',
    accent: 'text-orange-300',
    pillBg: 'bg-slate-900/85',
    pillText: 'text-orange-100',
    numberText: 'text-white',
  },
};

const PlayerBadge = ({ player, side, cardPick, scorerPick, namePosition = 'bottom' }) => {
  const colors = HALF_STYLE[side];
  const isCarded = cardPick && sameLastName(cardPick.player, player.name);
  const isScorer = scorerPick && sameLastName(scorerPick.player, player.name);
  const label = lastName(player.name) || player.name || '—';
  const number = player.number;
  const badges = [];
  if (isCarded) badges.push(`Rischio giallo: ${cardPick?.reason || ''}`);
  if (isScorer) badges.push(`Probabile marcatore: ${scorerPick?.reason || ''}`);

  // iter171 — Il portiere sta appiccicato al bordo del pitch (y=4% o
  // y=96%): mettere il nome SOTTO il suo pallino lo fa finire sopra i
  // pallini dei difensori (nomi illeggibili — feedback utente).
  // Con `namePosition='right'` la pillola sta a fianco del pallino,
  // fuori dalla verticale dei difensori.
  const nameClass =
    namePosition === 'right'
      ? `absolute left-full top-1/2 -translate-y-1/2 ml-[6px] px-1.5 py-[1px] rounded-[3px] text-[9px] sm:text-[10px] font-semibold tracking-tight ${colors.pillBg} ${colors.pillText} shadow-[0_1px_2px_rgba(0,0,0,0.7)] max-w-[74px] truncate leading-[12px] whitespace-nowrap`
      : `absolute left-1/2 -translate-x-1/2 top-full mt-[3px] px-1.5 py-[1px] rounded-[3px] text-[9px] sm:text-[10px] font-semibold tracking-tight ${colors.pillBg} ${colors.pillText} shadow-[0_1px_2px_rgba(0,0,0,0.7)] max-w-[74px] truncate leading-[12px] whitespace-nowrap`;

  return (
    <div
      className="relative select-none"
      data-testid={`pitch-player-${number || 'X'}`}
      title={badges.join(' • ') || label}
    >
      {/* Badge — the outer wrapper has ONLY the badge's box so its
          `translate(-50%, -50%)` puts the badge center exactly on the
          computed pitch coordinate. The name pill is absolutely
          positioned right below and doesn't influence the anchor. */}
      <div
        className={`pitch-badge-circle w-[26px] h-[26px] sm:w-[30px] sm:h-[30px] rounded-full bg-gradient-to-b ${colors.shirt} shadow-[0_2px_5px_rgba(0,0,0,0.55)] flex items-center justify-center`}
        data-side={side}
      >
        <span className={`pitch-badge-number relative text-[10px] sm:text-[11px] font-black leading-none ${colors.numberText} drop-shadow-[0_1px_1px_rgba(0,0,0,0.7)]`}>
          {number ?? ''}
        </span>
      </div>
      {isCarded && (
        <div
          className="absolute -top-1 -left-1 w-2.5 h-3 bg-yellow-400 rounded-[2px] border border-yellow-700 shadow"
          data-testid={`pitch-yellow-card-${number || 'X'}`}
          aria-label="Rischio cartellino"
        />
      )}
      {isScorer && (
        <div
          className="absolute -top-1 -right-1 w-3.5 h-3.5 rounded-full bg-white border border-black shadow flex items-center justify-center text-[8px] leading-none"
          data-testid={`pitch-scorer-${number || 'X'}`}
          aria-label="Probabile marcatore"
        >
          ⚽
        </div>
      )}
      <span className={nameClass}>{label}</span>
    </div>
  );
};

/** Positions a badge absolutely on the pitch. */
const PositionedPlayer = ({ x, y, namePosition, ...rest }) => (
  <div
    className="absolute -translate-x-1/2 -translate-y-1/2"
    style={{ left: `${x}%`, top: `${y}%` }}
  >
    <PlayerBadge {...rest} namePosition={namePosition} />
  </div>
);

/** Renders one team's players over the full pitch (absolute coords). */
const TeamOnPitch = ({ team, side, cardPick, scorerPick }) => {
  const parsed = splitByGrid(team.starting_xi || []) || splitByFormation(team.starting_xi || [], team.formation);

  // Fallback: flat grid if we can't parse the formation.
  if (!parsed) {
    const xi = team.starting_xi || [];
    const isHome = side === 'home';
    const bounds = isHome ? { start: 6, end: 46 } : { start: 54, end: 94 };
    return (
      <>
        {xi.slice(0, 11).map((p, i) => {
          const col = i % 4;
          const row = Math.floor(i / 4);
          const rows = Math.ceil(xi.length / 4);
          const y =
            bounds.start +
            ((bounds.end - bounds.start) * (row + 0.5)) / Math.max(rows, 1);
          let x = 15 + (70 * (col + 0.5)) / 4;
          // iter211 — mirror HOME per allineare "sinistra squadra" con
          // viewer's RIGHT (stessa logica del render con formation).
          if (side === 'home') x = 100 - x;
          return (
            <PositionedPlayer
              key={i}
              x={x}
              y={y}
              player={p}
              side={side}
              cardPick={cardPick}
              scorerPick={scorerPick}
            />
          );
        })}
      </>
    );
  }

  const rowsForSide = side === 'home' ? parsed.rows : [...parsed.rows].reverse();
  const rowYs = computeRowYs(side, rowsForSide.length);
  // iter175 — Portiere spostato di ~3% dentro il campo (era 4%/96%
  // → il pallino sporgeva sulla linea di fondo). Con il pitch più
  // alto (60:105) c'è spazio per riportare tutti i nomi SOTTO al
  // pallino, portieri compresi (`namePosition` default).
  const gkY = side === 'home' ? 7 : 93;

  return (
    <>
      {/* Goalkeeper - dentro l'area di rigore. Con il pitch più alto
          (iter175) c'è spazio per rimettere il nome SOTTO il pallino
          come per gli altri giocatori. */}
      {parsed.gk && (
        <PositionedPlayer
          x={50}
          y={gkY}
          player={parsed.gk}
          side={side}
          cardPick={cardPick}
          scorerPick={scorerPick}
        />
      )}
      {/* Outfield rows */}
      {rowsForSide.map((row, ridx) => {
        const y = rowYs[ridx];
        const n = row.length;
        return row.map((p, pidx) => {
          // Horizontal spread narrows for rows with few players so that,
          // e.g., 2 forwards stand close to the central channel (like in
          // diretta.it / SofaScore) instead of at the touchlines.
          //   n=1 → 50
          //   n=2 → 34, 66    (central pair, ~32% from center)
          //   n=3 → 22, 50, 78
          //   n≥4 → 10..90 evenly (full width, wingers hug the touchline)
          let x;
          if (n === 1) x = 50;
          else if (n === 2) x = pidx === 0 ? 34 : 66;
          else if (n === 3) x = [22, 50, 78][pidx];
          else x = 10 + (80 * pidx) / (n - 1);
          // Rows are ordered from the team's own left to its right. HOME is on top and attacks
          // down, so its left is on the viewer's right (mirror); AWAY attacks up (no mirror).
          if (side === 'home') x = 100 - x;
          return (
            <PositionedPlayer
              key={`${ridx}-${pidx}`}
              x={x}
              y={y}
              player={p}
              side={side}
              cardPick={cardPick}
              scorerPick={scorerPick}
            />
          );
        });
      })}
    </>
  );
};

/** Vector-drawn pitch background (stripes + markings). */
const PitchBackground = () => (
  <>
    {/* Grass stripes — 10 horizontal bands */}
    <div className="absolute inset-0">
      {Array.from({ length: 10 }).map((_, i) => (
        <div
          key={i}
          className="absolute inset-x-0"
          style={{
            top: `${i * 10}%`,
            height: '10%',
            background:
              i % 2 === 0
                ? 'linear-gradient(180deg,#0f6d34 0%,#0d5d2d 100%)'
                : 'linear-gradient(180deg,#0e6631 0%,#0c5528 100%)',
          }}
        />
      ))}
    </div>
    {/* Ambient rim light */}
    <div
      className="absolute inset-0 pointer-events-none"
      style={{
        boxShadow:
          'inset 0 0 60px rgba(0,0,0,0.55), inset 0 0 120px rgba(0,0,0,0.25)',
      }}
    />
    {/* Markings via SVG for crispness */}
    <svg
      className="absolute inset-0 w-full h-full pointer-events-none"
      viewBox="0 0 200 300"
      preserveAspectRatio="none"
    >
      <g fill="none" stroke="rgba(255,255,255,0.75)" strokeWidth="0.7">
        {/* Outer border */}
        <rect x="4" y="4" width="192" height="292" rx="1.5" />
        {/* Halfway line */}
        <line x1="4" y1="150" x2="196" y2="150" />
        {/* Center circle + spot */}
        <circle cx="100" cy="150" r="20" />
        <circle cx="100" cy="150" r="1.4" fill="rgba(255,255,255,0.9)" />
        {/* Top penalty box (home defends top → own box at top) */}
        <rect x="45" y="4" width="110" height="34" />
        <rect x="70" y="4" width="60" height="12" />
        <circle cx="100" cy="26" r="1.4" fill="rgba(255,255,255,0.9)" />
        {/* Top D-arc */}
        <path d="M84,38 A20,20 0 0 0 116,38" />
        {/* Bottom penalty box */}
        <rect x="45" y="262" width="110" height="34" />
        <rect x="70" y="284" width="60" height="12" />
        <circle cx="100" cy="274" r="1.4" fill="rgba(255,255,255,0.9)" />
        {/* Bottom D-arc */}
        <path d="M84,262 A20,20 0 0 1 116,262" />
        {/* Corner arcs */}
        <path d="M4,7 A3,3 0 0 1 7,4" />
        <path d="M196,7 A3,3 0 0 0 193,4" />
        <path d="M4,293 A3,3 0 0 0 7,296" />
        <path d="M196,293 A3,3 0 0 1 193,296" />
      </g>
    </svg>
  </>
);

/** Recap under the pitch: card + scorer picks per team. */
const PredictionRecap = ({ prediction, homeTeam, awayTeam, loading, revealed, onReveal, revealing, justRevealed }) => {
  const revealMode = Array.isArray(revealed);
  // iter219c (2026-02) — Skeleton loader mentre `build_card_prediction`
  // gira on-demand (~15-30s per partite fuori dal pre-cache dei top-10).
  // Senza skeleton l'utente vede il pitch senza recap → può pensare che
  // non ci sia il pronostico. Con skeleton capisce che sta caricando.
  if (loading && !prediction && !revealMode) {
    return (
      <div data-testid="pitch-prediction-recap-loading" className="mt-3">
        <div className="flex justify-center">
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-gradient-to-r from-amber-500/25 to-orange-500/25 border border-amber-400/40 text-[10px] font-bold uppercase tracking-widest text-amber-300">
            <span aria-hidden>⭐</span> Top Pick della partita
          </span>
        </div>
        <div className="mt-3 space-y-2 animate-pulse">
          <div className="flex items-center gap-1.5">
            <span className="inline-block w-2.5 h-3 bg-yellow-400/40 rounded-sm" />
            <div className="h-2.5 w-40 rounded bg-white/10" />
          </div>
          <div className="h-3 w-64 rounded bg-white/5" />
          <div className="h-2.5 w-48 rounded bg-white/5" />
        </div>
        <div className="mt-4 flex items-center justify-center gap-2 text-[11px] text-amber-300/70">
          <svg className="animate-spin w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeOpacity="0.25" strokeWidth="3" />
            <path d="M22 12a10 10 0 0 0-10-10" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
          </svg>
          <span>Genero pronostico marcatore e cartellino…</span>
        </div>
      </div>
    );
  }
  const hasPred = Boolean(prediction && prediction.home && prediction.away);
  if (!hasPred && !revealMode) return null;
  const showCard = hasPred && (!revealMode || revealed.includes('card'));
  const showScorer = hasPred && (!revealMode || revealed.includes('scorer'));
  // iter231 — Clamp 0-100% come safety net per proteggere l'UI da
  // eventuali valori fuori scala (backend legacy che manda `40` int
  // invece di `0.4` float). Normalizza: se il valore è > 1, assumiamo
  // sia già in scala 0-100 e dividiamo per 100 prima del cast a %.
  const fmtPct = (p) => {
    let v = Number(p) || 0;
    if (v > 1) v = v / 100;
    v = Math.max(0, Math.min(1, v));
    return `${Math.round(v * 100)}%`;
  };

  const _pick = (side, kind) => {
    const block = prediction?.[side];
    return (block && block[kind]) || (kind === 'card' ? block : null);
  };

  const cardHome = _pick('home', 'card');
  const cardAway = _pick('away', 'card');
  const scorerHome = _pick('home', 'scorer');
  const scorerAway = _pick('away', 'scorer');

  const Section = ({ icon, title, iconClass, rows }) => (
    <div className="mt-3 border-t border-white/10 pt-3 space-y-1.5">
      <p className={`text-[10px] uppercase tracking-widest font-bold flex items-center gap-1.5 ${iconClass}`}>
        {icon}
        {title}
      </p>
      {rows.map((r) =>
        r?.player ? (
          <div
            key={r.side}
            className="text-xs text-gray-200"
            data-testid={`recap-${title.replace(/\s+/g, '-').toLowerCase()}-${r.side}`}
          >
            <span className={`font-bold ${r.side === 'home' ? 'text-cyan-300' : 'text-orange-300'}`}>
              {r.team}:
            </span>{' '}
            <span className="font-semibold">{r.player}</span>
            <span className={`ml-1 ${iconClass}`}>({fmtPct(r.probability)})</span>
            <p className="text-[11px] text-gray-400 leading-snug mt-0.5">{r.reason}</p>
          </div>
        ) : null
      )}
    </div>
  );

  return (
    <div data-testid="pitch-prediction-recap">
      {/* iter167 — Badge esplicito: rende chiaro che le due voci
          (cartellino + marcatore) sono le più probabili di TUTTA la
          partita, non una per squadra. */}
      <div
        className="mt-3 flex justify-center"
        data-testid="top-pick-badge"
      >
        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-gradient-to-r from-amber-500/25 to-orange-500/25 border border-amber-400/40 text-[10px] font-bold uppercase tracking-widest text-amber-300">
          <span aria-hidden>⭐</span> Top Pick della partita
        </span>
      </div>
      {showCard && <div className={`reveal-fade ${justRevealed === 'card' ? 'reveal-pending' : ''}`} data-testid="recap-card-wrap"><Section
        icon={<span className="inline-block w-2.5 h-3 bg-yellow-400 rounded-sm border border-yellow-600" />}
        title="Cartellino — chi rischia"
        iconClass="text-yellow-300"
        rows={[
          { side: 'home', team: homeTeam, ...(cardHome || {}) },
          { side: 'away', team: awayTeam, ...(cardAway || {}) },
        ]}
      /></div>}
      {showScorer && (scorerHome || scorerAway) && (
        <div className={`reveal-fade ${justRevealed === 'scorer' ? 'reveal-pending' : ''}`} data-testid="recap-scorer-wrap">
          <Section
            icon={<span>⚽</span>}
            title="Probabile marcatore"
            iconClass="text-white"
            rows={[
              { side: 'home', team: homeTeam, ...(scorerHome || {}) },
              { side: 'away', team: awayTeam, ...(scorerAway || {}) },
            ]}
          />
        </div>
      )}
      {showScorer && !(scorerHome || scorerAway) && (
        <p className={`mt-3 border-t border-white/10 pt-3 text-[11px] text-gray-400 ${`reveal-fade ${justRevealed === 'scorer' ? 'reveal-pending' : ''}`}`} data-testid="recap-no-scorer">
          Nessun marcatore previsto per questa partita.
        </p>
      )}
      {revealMode && (
        <RevealButtons revealed={revealed} onReveal={onReveal} revealing={revealing} />
      )}
    </div>
  );
};

const RevealButtons = ({ revealed, onReveal, revealing }) => {
  const items = [
    { kind: 'card', label: 'Scopri il probabile ammonito', icon: <span className="inline-block w-3.5 h-5 bg-yellow-300 rounded-[3px] border-2 border-[#1A1A1A] rotate-[-8deg]" /> },
    { kind: 'scorer', label: 'Scopri il probabile marcatore', icon: <span aria-hidden className="text-lg leading-none">⚽</span> },
  ].filter((i) => !revealed.includes(i.kind));
  if (!items.length) return null;
  return (
    <div className="mt-3 flex flex-col gap-2" data-testid="card-reveal-buttons">
      {items.map((i) => (
        <button
          key={i.kind}
          onClick={() => onReveal(i.kind)}
          disabled={Boolean(revealing)}
          className="reveal-glow w-full rounded-2xl bg-gradient-to-r from-[#F5A623] to-[#FF6B6B] px-4 py-3.5 text-[15px] font-black uppercase tracking-wide text-[#1A1A1A] flex items-center justify-center gap-2.5 transition-transform active:scale-[0.97] disabled:opacity-70"
          style={{ fontFamily: 'Barlow Condensed' }}
          data-testid={`reveal-${i.kind}-btn`}
        >
          {revealing === i.kind ? <span className="w-4 h-4 border-2 border-[#1A1A1A] border-t-transparent rounded-full animate-spin" /> : i.icon}
          {i.label}
        </button>
      ))}
    </div>
  );
};

const LineupsPitch = ({ lineups, cardPrediction, cardQuota, cardLoading, isPredicted, revealed = null, onReveal, revealing, justRevealed = null }) => {
  const home = lineups?.[0];
  const away = lineups?.[1];

  // iter182 — Filter card/scorer picks against the actual XI on the
  // pitch. Anything that doesn't fuzzy-match the shown players is
  // discarded (prevents "Juan"/"M. Diagne" tickets when the backend
  // cache is stale relative to the currently displayed lineup).
  const safeCardPrediction = useMemo(() => {
    if (!cardPrediction) return null;
    const homeXi = (home?.starting_xi || []).map((p) => p.name).filter(Boolean);
    const awayXi = (away?.starting_xi || []).map((p) => p.name).filter(Boolean);
    const clean = (block, xiNames) => {
      if (!block || typeof block !== 'object') return block;
      const out = { ...block };
      for (const kind of ['card', 'scorer']) {
        const pick = out[kind];
        if (pick && pick.player && !fuzzyInXi(pick.player, xiNames)) {
          out[kind] = null;
        }
      }
      // legacy shape: block itself IS the card payload
      if (out.player && !fuzzyInXi(out.player, xiNames)) {
        delete out.player;
        delete out.probability;
        delete out.reason;
      }
      return out;
    };
    const cleaned = {
      ...cardPrediction,
      home: clean(cardPrediction.home, homeXi),
      away: clean(cardPrediction.away, awayXi),
    };

    // iter209 (2026-02) — Un solo cartellino per partita: tra home.card
    // e away.card teniamo quello con `probability` più alta. Il perdente
    // viene azzerato così NON compare né sul pitch (emoji) né nel recap.
    // Stessa logica applicata al probabile marcatore (iter209b).
    const homeCardProb = Number(cleaned.home?.card?.probability) || 0;
    const awayCardProb = Number(cleaned.away?.card?.probability) || 0;
    if (cleaned.home?.card && cleaned.away?.card) {
      if (homeCardProb >= awayCardProb) {
        cleaned.away = { ...cleaned.away, card: null };
      } else {
        cleaned.home = { ...cleaned.home, card: null };
      }
    }

    const homeScorerProb = Number(cleaned.home?.scorer?.probability) || 0;
    const awayScorerProb = Number(cleaned.away?.scorer?.probability) || 0;
    if (cleaned.home?.scorer && cleaned.away?.scorer) {
      if (homeScorerProb >= awayScorerProb) {
        cleaned.away = { ...cleaned.away, scorer: null };
      } else {
        cleaned.home = { ...cleaned.home, scorer: null };
      }
    }

    return cleaned;
  }, [cardPrediction, home, away]);

  if (!lineups || lineups.length !== 2) return null;

  const _extract = (side, kind) => {
    const b = safeCardPrediction?.[side];
    if (!b) return null;
    if (b[kind]) return b[kind];
    return kind === 'card' ? b : null;
  };
  const _shown = (kind) => !Array.isArray(revealed) || revealed.includes(kind);
  const homeCard = _shown('card') ? _extract('home', 'card') : null;
  const awayCard = _shown('card') ? _extract('away', 'card') : null;
  const homeScorer = _shown('scorer') ? _extract('home', 'scorer') : null;
  const awayScorer = _shown('scorer') ? _extract('away', 'scorer') : null;

  return (
    <div
      className="rv-dark rounded-2xl p-3 border border-emerald-500/30 bg-gradient-to-br from-slate-950/80 to-emerald-950/60 shadow-[0_10px_40px_rgba(0,0,0,0.4)]"
      data-testid="lineups-pitch"
    >
      {/* Team headers — team name on top line, formation on the line
          below, so long team names like "Nottingham Forest" never wrap
          together with the module. */}
      <div className="flex items-start justify-between mb-2 px-1 gap-2">
        <div className="flex items-start gap-2 min-w-0 flex-1">
          <span className="mt-1 w-2.5 h-2.5 rounded-full bg-cyan-400 shadow-[0_0_8px_rgba(34,211,238,0.9)] shrink-0" />
          <div className="min-w-0">
            <div className="text-xs text-cyan-300 font-bold uppercase tracking-widest truncate leading-tight">
              {home.team_name}
            </div>
            <div className="text-[10px] text-white/50 font-normal leading-tight">
              {home.formation || '—'}
            </div>
          </div>
        </div>
        <div className="flex items-start gap-2 min-w-0 flex-1 justify-end">
          <div className="min-w-0 text-right">
            <div className="text-xs text-orange-300 font-bold uppercase tracking-widest truncate leading-tight">
              {away.team_name}
            </div>
            <div className="text-[10px] text-white/50 font-normal leading-tight">
              {away.formation || '—'}
            </div>
          </div>
          <span className="mt-1 w-2.5 h-2.5 rounded-full bg-orange-400 shadow-[0_0_8px_rgba(251,146,60,0.9)] shrink-0" />
        </div>
      </div>

      {isPredicted && (
        <div className="mb-2 text-center">
          <span
            className="text-[9px] px-2 py-0.5 rounded-full bg-amber-500/20 border border-amber-400/40 text-amber-300 font-bold uppercase tracking-widest"
            data-testid="pitch-predicted-badge"
          >
            Probabili
          </span>
        </div>
      )}

      {/* The pitch — real football pitch proportions (68 x 105 ≈ 2:3),
          same as diretta.it. The background is clipped by an inner
          wrapper (rounded corners) while player badges float on top with
          overflow-visible so the goalkeeper's name never gets cropped
          at the byline. */}
      <div
        className={`relative mx-auto ${justRevealed ? `reveal-hide-${justRevealed}` : ''}`}
        style={{
          // iter175 — Pitch più alto (60:105 invece di 68:105): guadagniamo
          // ~12% di altezza per distanziare verticalmente le righe e
          // far rientrare i portieri dentro l'area di rigore invece che
          // a cavallo della linea di fondo campo.
          aspectRatio: '60 / 105',
          maxWidth: 460,
          width: '100%',
        }}
      >
        <div className="absolute inset-0 rounded-xl overflow-hidden">
          <PitchBackground />
        </div>
        <div className="absolute inset-0">
          <TeamOnPitch
            team={home}
            side="home"
            cardPick={homeCard}
            scorerPick={homeScorer}
          />
          <TeamOnPitch
            team={away}
            side="away"
            cardPick={awayCard}
            scorerPick={awayScorer}
          />
        </div>
      </div>

      <PredictionRecap
        prediction={safeCardPrediction}
        homeTeam={home.team_name}
        awayTeam={away.team_name}
        loading={cardLoading && !safeCardPrediction}
        revealed={revealed}
        onReveal={onReveal}
        revealing={revealing}
        justRevealed={justRevealed}
      />

      {/* iter192 — Quota card+scorer per utenti base (3/giorno). */}
      <CardQuotaFooter quota={cardQuota} hasPrediction={Boolean(safeCardPrediction)} revealMode={Array.isArray(revealed)} />
    </div>
  );
};


// iter192 — Footer sotto il pitch che mostra il contatore ("2 di 3")
// per gli utenti base e la CTA "Diventa VIP" quando esauriscono la quota.
// iter239 — Aggiunta anche la CTA "Invita 3 amici" accanto al VIP.
// VIP/GOLD/admin non vedono nulla (is_unlimited=true).
const CardQuotaFooter = ({ quota, hasPrediction, revealMode = false }) => {
  const navigate = useNavigate();
  const [showInviteModal, setShowInviteModal] = React.useState(false);

  if (!quota || quota.is_unlimited) return null;

  const goToChat = () => {
    navigate('/chat', {
      state: {
        prefilledMessage: 'Voglio accedere alle analisi VIP',
      },
    });
  };

  // Caso 1 — cap raggiunto (4° partita in poi): mostra 2 CTA (VIP + Invita).
  // Reveal buttons: at the cap the popup opens on click, no inline CTA.
  if (quota.upgrade_required && !revealMode) {
    return (
      <>
        <div
          data-testid="card-quota-upgrade-cta"
          className="mt-3 rounded-2xl border border-amber-500/30 bg-gradient-to-br from-amber-500/10 via-orange-500/10 to-transparent p-4 text-center"
        >
          <div className="mb-2 text-2xl">👑</div>
          <p className="mb-1 text-sm font-semibold text-amber-200">
            Hai visto {quota.max} probabili marcatori e cartellini nelle ultime 24 ore
          </p>
          <p className="mb-3 text-xs text-white/70">
            Scegli come continuare a vederli 👇
          </p>
          <div className="flex flex-col gap-2">
            <button
              data-testid="card-quota-become-vip-btn"
              onClick={goToChat}
              className="w-full rounded-full bg-gradient-to-r from-amber-400 to-orange-500 px-5 py-2 text-sm font-bold text-black shadow-lg transition hover:from-amber-300 hover:to-orange-400"
            >
              👑 Diventa membro VIP
            </button>
            <button
              data-testid="card-quota-invite-btn"
              onClick={() => setShowInviteModal(true)}
              className="w-full rounded-full bg-[#1A1A1A] border border-emerald-500/40 px-5 py-2 text-sm font-bold text-emerald-300 hover:border-emerald-400 hover:bg-emerald-500/10 transition"
            >
              👥 Invita amici (7 giorni gratis)
            </button>
          </div>
        </div>
        <InviteFriendsModal
          open={showInviteModal}
          onClose={() => setShowInviteModal(false)}
        />
      </>
    );
  }

  // Caso 2 — sotto il cap ma c'è un pronostico: mostra il contatore.
  if (hasPrediction && quota.max > 0) {
    return (
      <div
        data-testid="card-quota-counter"
        className="mt-3 flex items-center justify-center gap-2 text-xs text-white/60"
      >
        <span>Pronostico marcatori e cartellini</span>
        <span className="rounded-full bg-white/10 px-2 py-0.5 font-semibold text-white/90">
          {quota.used} di {quota.max}
        </span>
      </div>
    );
  }

  return null;
};

export default LineupsPitch;
